import { deleteDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadString,
} from "firebase/storage";
import { auth, db } from "../firebase";

// Get Firebase Functions instance
const functions = getFunctions();

/**
 * Email Service for invoice management
 * Uses Firebase Cloud Functions for email sending via Gmail SMTP
 */
export class EmailService {
  /**
   * Send email via Firebase Cloud Function
   * This is a generic method for backward compatibility with existing code
   * @param {Object} mailOptions - { to, subject, text, invoiceNumber, etc. }
   * @returns {Promise<Object>} - Result of email sending operation
   */
  async _sendEmail(mailOptions) {
    try {
      console.log("📧 Sending email via Firebase Function:", {
        to: mailOptions.to,
        subject: mailOptions.subject,
        hasMessage: !!mailOptions.text,
      });

      // If we have an invoice ID, use the proper function
      if (mailOptions.invoiceId) {
        const sendInvoiceEmailFn = httpsCallable(functions, "sendInvoiceEmail");
        const result = await sendInvoiceEmailFn({
          invoiceId: mailOptions.invoiceId,
          recipientEmail: mailOptions.to,
          subject: mailOptions.subject,
          message: mailOptions.text,
        });
        console.log("✅ Email sent via sendInvoiceEmail:", result.data);
        return { success: true, message: "Email sent successfully", ...result.data };
      }

      // For generic emails without invoice context, log a warning
      // The Firebase functions expect invoice/proposal IDs
      console.warn("⚠️ _sendEmail called without invoiceId - email may not be sent");
      console.warn("Consider using sendInvoiceEmail, sendPaymentReceipt, or sendReminder directly");

      return {
        success: false,
        message: "Generic email sending requires an invoice or proposal ID"
      };
    } catch (error) {
      console.error("❌ Error sending email via Firebase Function:", error);
      throw new Error(
        `Failed to send email: ${error?.message || "Unknown error"}`
      );
    }
  }

  /**
   * Send invoice email to client via Firebase Cloud Function
   * @param {Object} invoice - The invoice data
   * @param {Object} emailData - Email content and options
   * @param {string} emailData.recipientEmail - Recipient email address
   * @param {string} emailData.subject - Email subject
   * @param {string} emailData.message - Email message body
   * @param {boolean} emailData.attachPDF - Whether to attach PDF (handled server-side)
   * @param {string} [pdfBase64] - PDF content as base64 string (optional, for legacy support)
   * @returns {Promise<Object>} - Result of email sending operation
   */
  async sendInvoiceEmail(invoice, emailData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("You must be signed in to send invoices");
      }

      // Validate inputs
      if (!emailData.recipientEmail) {
        throw new Error("Recipient email is required");
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailData.recipientEmail)) {
        throw new Error("Please provide a valid email address");
      }

      console.log("🚀 Starting invoice email send via Firebase Function...");
      console.log("📧 Recipient:", emailData.recipientEmail);
      console.log("📄 Invoice ID:", invoice.id);
      console.log("📄 Invoice Number:", invoice.invoiceNumber);

      // Ensure invoice has an ID - if not, save it first
      if (!invoice.id) {
        throw new Error("Invoice must be saved before sending. Please save the invoice first.");
      }

      // Call Firebase Cloud Function
      const sendInvoiceEmailFn = httpsCallable(functions, "sendInvoiceEmail");

      const result = await sendInvoiceEmailFn({
        invoiceId: invoice.id,
        recipientEmail: emailData.recipientEmail,
        subject: emailData.subject,
        message: emailData.message,
        ccEmails: emailData.ccEmails || [],
      });

      console.log("✅ Firebase Function response:", result.data);

      // The Cloud Function handles:
      // - Fetching invoice from Firestore
      // - Rendering email template
      // - Sending via Gmail SMTP
      // - Updating invoice status to 'sent'
      // - Logging email activity

      // Handle draft cleanup on client side if needed
      if (result.data?.success && invoice.id) {
        try {
          const draftRef = doc(db, 'users', user.uid, 'invoiceDrafts', invoice.id);
          await deleteDoc(draftRef);
          console.log('Draft removed from invoiceDrafts:', invoice.id);
        } catch (delErr) {
          // Draft may not exist, that's fine
          console.log('No draft to remove or already removed');
        }
      }

      return {
        success: result.data?.success || false,
        message: result.data?.message || "Email sent successfully",
        messageId: result.data?.messageId,
      };

    } catch (error) {
      console.error("❌ Error in sendInvoiceEmail:", error);

      // Handle Firebase function errors
      const errorMessage = error.message || error.details || "Failed to send email";

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Upload PDF to Firebase Storage and get the download URL
   * @param {string} invoiceId - Invoice ID
   * @param {string} pdfBase64 - PDF content as base64 string
   * @returns {Promise<string>} - Download URL
   */
  async uploadPdfAndGetUrl(invoiceId, pdfBase64) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Authentication required");

      // For development, simulate the upload
      if (process.env.NODE_ENV === "development") {
        console.log("🔧 Development mode: Simulating PDF upload...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return `https://mock-storage.example.com/users/${user.uid}/invoices/${invoiceId}/invoice-${Date.now()}.pdf`;
      }

      const storage = getStorage();
      const path = `users/${user.uid}/invoices/${invoiceId}/invoice-${Date.now()}.pdf`;
      const fileRef = storageRef(storage, path);

      // Remove the data URL prefix if present
      const base64Content = pdfBase64.split(",")[1] || pdfBase64;

      // Upload as base64 string
      await uploadString(fileRef, base64Content, "base64");

      // Get the download URL
      const downloadUrl = await getDownloadURL(fileRef);
      return downloadUrl;
    } catch (error) {
      console.error("Error uploading PDF:", error);
      if (process.env.NODE_ENV === "development") {
        console.warn("📎 Development mode: Falling back to mock URL");
        const user = auth.currentUser;
        return `https://mock-storage.example.com/users/${user?.uid || "unknown"}/invoices/${invoiceId}/invoice-${Date.now()}.pdf`;
      }
      throw new Error(`Failed to upload PDF: ${error.message}`);
    }
  }

  /**
   * Update invoice status in Firestore
   * @param {string} userId - User ID
   * @param {string} invoiceId - Invoice ID
   * @param {string} status - New status (sent, viewed, paid, etc.)
   */
  async updateInvoiceStatus(userId, invoiceId, status) {
    try {
      const invoiceRef = doc(db, "users", userId, "invoices", invoiceId);
      try {
        // Try to update existing invoice
        await updateDoc(invoiceRef, {
          status,
          lastUpdated: new Date().toISOString(),
        });
      } catch (err) {
        // If the document does not exist, create it with minimal fields instead of failing
        // This handles cases where a draft wasn't promoted to the invoices collection yet.
        console.warn('updateInvoiceStatus: update failed, attempting to create invoice doc instead', err.message);
        try {
          await setDoc(invoiceRef, {
            id: invoiceId,
            status,
            lastUpdated: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            userId: userId,
          });
        } catch (createErr) {
          console.error('updateInvoiceStatus: failed to create invoice doc after update failure', createErr);
          throw createErr;
        }
      }
    } catch (error) {
      console.error("Error updating invoice status:", error);
      throw new Error(`Failed to update invoice status: ${error.message}`);
    }
  }

  /**
   * Send invoice receipt to client after payment
   * @param {Object} invoice - Invoice data
   * @param {Object} paymentData - Payment details
   * @returns {Promise<Object>} - Result of operation
   */
  async sendPaymentReceipt(invoice, paymentData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      // Validate inputs
      if (!invoice.id) {
        throw new Error("Invoice ID is required");
      }
      if (!paymentData.amount || !paymentData.date || !paymentData.method) {
        throw new Error("Payment details (amount, date, method) are required");
      }

      console.log("🧾 Sending payment receipt via Firebase Function...");
      console.log("📄 Invoice ID:", invoice.id);
      console.log("💰 Amount:", paymentData.amount);

      // Call Firebase Cloud Function
      const sendPaymentReceiptFn = httpsCallable(functions, "sendPaymentReceipt");

      const result = await sendPaymentReceiptFn({
        invoiceId: invoice.id,
        paymentData: {
          amount: paymentData.amount,
          date: paymentData.date,
          method: paymentData.method,
          reference: paymentData.reference || null,
        },
        recipientEmail: invoice.client?.email,
      });

      console.log("✅ Payment receipt sent:", result.data);

      return {
        success: result.data?.success || false,
        message: result.data?.message || "Receipt sent successfully",
        messageId: result.data?.messageId,
      };

    } catch (error) {
      console.error("Error sending payment receipt:", error);
      return {
        success: false,
        message: error.message || "Failed to send payment receipt",
      };
    }
  }

  /**
   * Send a reminder email for an invoice
   * @param {string} invoiceId - Invoice ID
   * @param {string} reminderType - Type of reminder: 'upcoming', 'due_today', 'overdue'
   * @param {string} [recipientEmail] - Optional recipient email override
   * @returns {Promise<Object>} - Result of operation
   */
  async sendReminder(invoiceId, reminderType = 'upcoming', recipientEmail = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      console.log("⏰ Sending reminder via Firebase Function...");
      console.log("📄 Invoice ID:", invoiceId);
      console.log("📋 Reminder Type:", reminderType);

      const sendReminderFn = httpsCallable(functions, "sendReminder");

      const result = await sendReminderFn({
        invoiceId,
        reminderType,
        recipientEmail,
      });

      console.log("✅ Reminder sent:", result.data);

      return {
        success: result.data?.success || false,
        message: result.data?.message || "Reminder sent successfully",
        messageId: result.data?.messageId,
      };

    } catch (error) {
      console.error("Error sending reminder:", error);
      return {
        success: false,
        message: error.message || "Failed to send reminder",
      };
    }
  }

  /**
   * Send a proposal email
   * @param {Object} proposal - Proposal data with id
   * @param {Object} emailData - Email options
   * @returns {Promise<Object>} - Result of operation
   */
  async sendProposalEmail(proposal, emailData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      if (!proposal.id) {
        throw new Error("Proposal ID is required");
      }

      console.log("📋 Sending proposal via Firebase Function...");
      console.log("📄 Proposal ID:", proposal.id);

      const sendProposalEmailFn = httpsCallable(functions, "sendProposalEmail");

      const result = await sendProposalEmailFn({
        proposalId: proposal.id,
        recipientEmail: emailData.recipientEmail,
        subject: emailData.subject,
        message: emailData.message,
        ccEmails: emailData.ccEmails || [],
      });

      console.log("✅ Proposal email sent:", result.data);

      return {
        success: result.data?.success || false,
        message: result.data?.message || "Proposal sent successfully",
        messageId: result.data?.messageId,
      };

    } catch (error) {
      console.error("Error sending proposal:", error);
      return {
        success: false,
        message: error.message || "Failed to send proposal",
      };
    }
  }
}

// Create and export a singleton instance
export const emailService = new EmailService();

// Export individual methods for easier imports with proper binding
export const sendInvoiceEmail = emailService.sendInvoiceEmail.bind(emailService);
export const sendPaymentReceipt = emailService.sendPaymentReceipt.bind(emailService);
export const sendReminder = emailService.sendReminder.bind(emailService);
export const sendProposalEmail = emailService.sendProposalEmail.bind(emailService);
